import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Factory, Plus, PlayCircle, CheckCircle2, XCircle, Trash2, Package, AlertTriangle, Boxes } from "lucide-react";

const STATUS = {
  planned: { l: "مُخطَّط", c: "bg-slate-100 text-slate-700", next: "in_progress", nextL: "بدء" },
  in_progress: { l: "قيد التنفيذ", c: "bg-amber-100 text-amber-700 border-amber-300", next: "completed", nextL: "إكمال" },
  completed: { l: "مكتمل", c: "bg-emerald-100 text-emerald-700", next: null },
  cancelled: { l: "ملغى", c: "bg-red-100 text-red-600", next: null },
};

export default function ProductionOrders() {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [boms, setBoms] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState({ product_id: "", quantity: 1, notes: "", components: [] });

  const pickFinished = (pid) => {
    const existing = boms.find(b => b.parent_product_id === pid)?.components || [];
    setForm(f => ({ ...f, product_id: pid, components: existing.length ? existing.map(c => ({ product_id: c.product_id, product_name: c.product_name, quantity: c.quantity })) : (f.components || []) }));
  };
  const addComp = () => setForm(f => ({ ...f, components: [...(f.components || []), { product_id: "", product_name: "", quantity: 1 }] }));
  const setComp = (i, pid) => { const p = products.find(x => x.id === pid); setForm(f => ({ ...f, components: f.components.map((c, x) => x === i ? { ...c, product_id: pid, product_name: p?.name || "" } : c) })); };
  const setCompQty = (i, q) => setForm(f => ({ ...f, components: f.components.map((c, x) => x === i ? { ...c, quantity: parseFloat(q) || 0 } : c) }));
  const rmComp = (i) => setForm(f => ({ ...f, components: f.components.filter((_, x) => x !== i) }));

  const load = async () => {
    try {
      const [o, p, b] = await Promise.all([api.get("/production/orders"), api.get("/products"), api.get("/boms")]);
      setOrders(o.data); setProducts(p.data); setBoms(b.data || []);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const producible = products.map(p => ({...p, bom: boms.find(b => b.parent_product_id === p.id)?.components || []})).filter(p => p.bom.length > 0);

  const save = async () => {
    if (!form.product_id) return toast.error("اختر المنتج النهائي");
    if (form.quantity <= 0) return toast.error("الكمية يجب أن تكون أكبر من صفر");
    const comps = (form.components || []).filter(c => c.product_id && c.quantity > 0);
    if (comps.length === 0) return toast.error("أضف مكوّناً واحداً على الأقل (المواد الخام)");
    try {
      await api.post("/production/orders", { product_id: form.product_id, quantity: form.quantity, notes: form.notes, components: comps });
      toast.success("تم إنشاء أمر الإنتاج");
      setDlg(false); setForm({ product_id: "", quantity: 1, notes: "", components: [] }); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const changeStatus = async (o, s) => {
    try { await api.post(`/production/orders/${o.id}/status`, { status: s }); toast.success("تم التحديث"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const del = async (o) => {
    if (!window.confirm(`حذف أمر إنتاج ${o.product_name}؟`)) return;
    try { await api.delete(`/production/orders/${o.id}`); load(); } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid="production-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2"><Factory className="w-6 h-6 text-primary" /> أوامر الإنتاج</h1>
          <p className="text-sm text-muted-foreground mt-1">تصنيع منتجات نهائية من BOM — خصم المكوّنات وإضافة الجاهز تلقائياً</p>
        </div>
        <Button onClick={() => setDlg(true)} className="gap-2" data-testid="new-po-btn"><Plus className="w-4 h-4" /> أمر إنتاج جديد</Button>
      </div>

      {producible.length === 0 && orders.length === 0 && (
        <div className="p-4 rounded border-2 border-dashed bg-blue-50 text-sm flex items-start gap-2">
          <Factory className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div><b>ابدأ بسهولة:</b> اضغط «أمر إنتاج جديد»، اختر المنتج النهائي، ثم أضف المواد الخام (المكوّنات) وكمية كل مادة — سيُحفظ ذلك تلقائياً كوصفة للمنتج ولن تحتاج لإعداد BOM منفصل.</div>
        </div>
      )}

      {orders.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed rounded-lg text-muted-foreground">
          <Factory className="w-16 h-16 mx-auto opacity-30 mb-3" />
          <div>لا توجد أوامر إنتاج</div>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b"><tr>
              <th className="text-start p-3">المنتج</th>
              <th className="text-center p-3">الكمية</th>
              <th className="text-center p-3">التكلفة المقدَّرة</th>
              <th className="text-center p-3">الحالة</th>
              <th className="text-start p-3">تاريخ الإنشاء</th>
              <th className="text-center p-3">إجراء</th>
            </tr></thead>
            <tbody>{orders.map(o => {
              const s = STATUS[o.status] || STATUS.planned;
              return (
                <tr key={o.id} className="border-b hover:bg-muted/30" data-testid={`po-${o.id}`}>
                  <td className="p-3 font-medium cursor-pointer hover:text-primary" onClick={() => setViewing(o)}>{o.product_name}</td>
                  <td className="p-3 text-center font-mono">{o.quantity}</td>
                  <td className="p-3 text-center font-mono text-xs">{o.estimated_cost?.toLocaleString("ar-EG")}</td>
                  <td className="p-3 text-center"><span className={`px-2 py-0.5 rounded text-xs ${s.c}`}>{s.l}</span></td>
                  <td className="p-3 text-xs font-mono text-muted-foreground">{new Date(o.created_at).toLocaleString("ar-EG")}</td>
                  <td className="p-3 text-center">
                    <div className="flex gap-1 justify-center flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => setViewing(o)} className="h-7 text-xs"><Boxes className="w-3 h-3 me-1" /> عرض</Button>
                      {s.next && <Button size="sm" onClick={() => changeStatus(o, s.next)} className="h-7 text-xs" data-testid={`advance-${o.id}`}>{s.nextL}</Button>}
                      {["planned","in_progress"].includes(o.status) && <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-red-500" onClick={() => changeStatus(o, "cancelled")}><XCircle className="w-3.5 h-3.5" /></Button>}
                      {o.status !== "completed" && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => del(o)}><Trash2 className="w-3.5 h-3.5" /></Button>}
                    </div>
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent data-testid="po-dialog">
          <DialogHeader><DialogTitle>أمر إنتاج جديد</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>المنتج النهائي (الجاهز)</Label>
              <select className="w-full h-10 px-2 border rounded bg-background text-sm" value={form.product_id} onChange={e => pickFinished(e.target.value)} data-testid="po-product">
                <option value="">— اختر المنتج —</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <p className="text-xs text-muted-foreground mt-1">اختر أي منتج، وأضف مكوّناته أدناه — تُحفظ كوصفة تلقائياً.</p>
            </div>
            <div><Label>الكمية المراد تصنيعها</Label><Input type="number" value={form.quantity} onChange={e => setForm({ ...form, quantity: parseFloat(e.target.value) || 0 })} data-testid="po-qty" /></div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="flex items-center gap-1"><Boxes className="w-4 h-4" /> المكوّنات (المواد الخام)</Label>
                <Button type="button" size="sm" variant="outline" onClick={addComp} className="h-7 text-xs gap-1" data-testid="po-add-comp"><Plus className="w-3 h-3" /> إضافة مكوّن</Button>
              </div>
              {(form.components || []).length === 0 && <p className="text-xs text-muted-foreground py-2">لا مكوّنات بعد — اضغط «إضافة مكوّن» لكل مادة خام تدخل في التصنيع.</p>}
              <div className="space-y-2">
                {(form.components || []).map((c, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`po-comp-${i}`}>
                    <select className="flex-1 h-9 px-2 border rounded bg-background text-sm" value={c.product_id} onChange={e => setComp(i, e.target.value)} data-testid={`po-comp-product-${i}`}>
                      <option value="">— مادة خام —</option>
                      {products.filter(p => p.id !== form.product_id).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <Input type="number" className="w-24 h-9" placeholder="كمية/وحدة" value={c.quantity} onChange={e => setCompQty(i, e.target.value)} data-testid={`po-comp-qty-${i}`} />
                    <Button type="button" size="icon" variant="ghost" className="h-9 w-9 text-red-500" onClick={() => rmComp(i)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">الكمية = كم من هذه المادة يلزم لصناعة وحدة واحدة من المنتج النهائي.</p>
            </div>
            <div><Label>ملاحظات</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDlg(false)}>إلغاء</Button><Button onClick={save} data-testid="save-po-btn">إنشاء</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewing} onOpenChange={o => !o && setViewing(null)}>
        <DialogContent className="max-w-2xl" data-testid="po-view-dialog">
          {viewing && (<>
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Factory className="w-5 h-5" /> {viewing.product_name} × {viewing.quantity}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="p-2 rounded bg-muted/30"><div className="text-muted-foreground">التكلفة الكلية</div><div className="font-bold text-primary">{viewing.estimated_cost?.toLocaleString("ar-EG")}</div></div>
                <div className="p-2 rounded bg-muted/30"><div className="text-muted-foreground">تكلفة الوحدة</div><div className="font-bold">{viewing.unit_cost?.toLocaleString("ar-EG")}</div></div>
                <div className="p-2 rounded bg-muted/30"><div className="text-muted-foreground">الحالة</div><div className="font-bold">{STATUS[viewing.status]?.l}</div></div>
              </div>
              <div>
                <h3 className="text-sm font-bold mb-2 flex items-center gap-1"><Boxes className="w-4 h-4" /> المكوّنات المطلوبة</h3>
                <div className="border rounded overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50"><tr><th className="text-start p-2">الاسم</th><th className="text-center p-2">للوحدة</th><th className="text-center p-2">الكلي</th><th className="text-center p-2">المخزون</th><th className="text-end p-2">التكلفة</th></tr></thead>
                    <tbody>{viewing.ingredients?.map((ing, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-2">{ing.name}</td>
                        <td className="p-2 text-center font-mono">{ing.unit_qty}</td>
                        <td className="p-2 text-center font-mono font-bold">{ing.total_qty}</td>
                        <td className={`p-2 text-center font-mono ${ing.current_stock < ing.total_qty ? "text-red-600 font-bold" : "text-emerald-600"}`}>{ing.current_stock}</td>
                        <td className="p-2 text-end font-mono">{ing.total_cost?.toLocaleString("ar-EG")}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
              {viewing.notes && <div className="p-2 rounded bg-muted/30 text-xs">{viewing.notes}</div>}
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
